import EventEmitter from 'events';
import debounce from 'lodash/debounce';

import {
    APP_TYPE_BROWSER,
    VIEW_MODE_PAS,
} from '../../constants/global-constants';
import cookieHelper from '../../helpers/cookie-helper';
import featureFlagsHelper from '../../helpers/featureFlags-helper';
import urlParser from '../../helpers/url-parser';
import webglDetectHelper from '../../helpers/webgl-detect-helper';
import analyticsServiceFactory from '../../services/analytics-service';
import eventManagerFactory from '../event-manager-factory';
import simmut from 'simmut';

const { parseQuery, parseUrl } = urlParser;

const { getServerProperty } = cookieHelper;

const { isWebGLPresent } = webglDetectHelper;

const _eventEmitter = new EventEmitter();
let _model = simmut.instance();
let _analyticsService;
let _eventManager = null;

const get = (...args) => _model.get(...args);
const set = (...args) => _model.set(...args);

const on = (eventName, handler) => {
    _eventEmitter.on(eventName, handler);
};

const off = (eventName, handler) => {
    _eventEmitter.removeListener(eventName, handler);
};

const getChildren = ({ path = '' }) => {
    const item = _model.get(path) || {};
    const keys = Object.keys(item);

    return keys;
};

const merge = (path, right) => {
    _model.merge(path, right);
};

const setView = ({ view, params }) => {
    _model.set('app.location.view', view);
    _model.set('app.location.params', params);
};

const getView = () => {
    const view = _model.get('app.location.view');
    const params = _model.get('app.location.params');

    return { params, view };
};

const setLoading = ({ scope, value }) => {
    _model.set(`loading.${scope}`, value);
};

const getIsLoading = () => {
    const loading = _model.get('loading');

    if (!loading) {
        return false;
    }

    const keys = Object.keys(loading);

    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const value = loading[key];

        if (value) {
            return true;
        }
    }
    return false;
};

// eslint-disable-next-line complexity
const setPageParamsFromUrl = (url) => {
    let urlToUse = url;

    if (!urlToUse) {
        if (typeof window === 'undefined' || !window.location) {
            urlToUse = '';
        } else {
            urlToUse = window.location.href;
        }
    }
    const { pathname = '', search = '' } = parseUrl(urlToUse) || {};

    const query = parseQuery(search);

    const pageParams = {
        cartId: '',
        complimentaryOrder: false,
        deliveryMethodOrder: '',
        eventId: '',
        insideIFrameProxy: false,
        overrideTicketQuantities: false,
        pos: 'online',
        promoCode: '',
        protocolVersion: 1,
        holdId: '',
        viewMode: VIEW_MODE_PAS,
        locale: null,
    };

    if (query.srcUrl) {
        const srcUrl = atob(query.srcUrl);

        setPageParamsFromUrl(srcUrl);
        return;
    }

    const parts = pathname.split('/');

    if (parts.length && !parts[parts.length - 1]) {
        parts.pop();
    }

    const lastPart = parts[parts.length - 1];

    // eslint-disable-next-line no-restricted-globals
    if (lastPart && !isNaN(+lastPart)) {
        pageParams.eventId = lastPart;
    } else if (query.eventId) {
        pageParams.eventId = query.eventId;
    }
    if (query['cart_uuid']) {
        pageParams.cartId = query['cart_uuid'];
    }
    if (query.discount) {
        pageParams.promoCode = query.discount;
    }
    if (query.insideIFrameProxy) {
        pageParams.insideIFrameProxy = !!query.insideIFrameProxy;
    }
    if (query['override_ticket_quantities']) {
        pageParams.overrideTicketQuantities =
            !!query['override_ticket_quantities'];
    }
    if (query['protocol_version']) {
        // protocol_version must be a number, so we convert it to a number if it exists
        pageParams.protocolVersion = +query['protocol_version'];
    }
    if (query.pos) {
        pageParams.pos = query.pos;
    }
    if (query['complimentary_order']) {
        pageParams.complimentaryOrder = !!query['complimentary_order'];
    }
    if (query['delivery_method_order']) {
        pageParams.deliveryMethodOrder = (
            query['delivery_method_order'] || ''
        ).split(',');
    }
    if (query['hold_id']) {
        pageParams.holdId = query['hold_id'];
    }
    if (query['view_mode']) {
        pageParams.viewMode = query['view_mode'];
    }
    if (query.locale) {
        pageParams.locale = query.locale;
    }
    _model.set('app.page-params', pageParams);
};

// PageParams are obtained from the url and then cached in the model
// under the key app.page-params.
// We can modify any url param programatically by changing app.page-params.
// Please note that calling director.resetModel() will clear any overrides

// eslint-disable-next-line complexity
const getPageParams = () => {
    const pageParams = _model.get('app.page-params') || {};

    return pageParams;
};

// This will overwrite any page-param which comes from the url with
// a new value in the model
const setPageParam = ({ key, value }) => {
    _model.set(`app.page-params.${key}`, value);
};

const getEventId = (url = null) => {
    const params = getPageParams(url);

    return params.eventId || '';
};

const resetModel = () => {
    _model = simmut.instance();
};

const _getModel = () => _model;

const trackAnalyticsAction = ({ action, category, label }) => {
    if (!_analyticsService) {
        return;
    }

    _analyticsService.trackAction({
        action,
        category,
        label,
    });
};

const getEventManager = () => _eventManager;

let _initialised = false;

const getAppType = () => APP_TYPE_BROWSER;

const appDirector = {
    _getModel,
    getAppType,
    getView,
    setView,
    set,
    get,
    getChildren,
    merge,
    getEventId,
    getEventManager,
    // We get true if any scope is loading
    getIsLoading,
    setPageParamsFromUrl,
    getPageParams,
    setPageParam,
    getServerProperty,
    off,
    on,
    resetModel,
    // We set loading for a scope
    setLoading,
    trackAnalyticsAction,
};

_eventManager = eventManagerFactory({
    director: appDirector,
    model: simmut.proxy({
        model: _model,
        prefix: 'app.currentEvent.data.seatmap',
    }),
});

const aInit = () => {
    if (_initialised) {
        return Promise.resolve();
    }

    _model.set('app.initialised', true);

    _model.on(
        'change',
        debounce(() => {
            _eventEmitter.emit('change');
        }),
    );

    const {
        eventId,
        cartId,
        promoCode,
        complimentaryOrder,
        deliveryMethodOrder,
        holdId,
        viewMode,
    } = getPageParams();

    _model.set('app.params.eventId', eventId);
    _model.set('app.params.cartId', cartId);

    if (eventId) {
        _eventManager.aInit({
            eventId,
            cartId,
            promoCode,
            complimentaryOrder,
            deliveryMethodOrder,
            holdId,
            viewMode,
        });
        setView({ view: 'seatmap' });
    } else {
        setView({ view: 'home' });
    }

    if (typeof window !== 'undefined') {
        // retrieve data from server to hydrate the client
        const serverData = window.__SERVER_DATA__ || {};
        const props = {
            ...serverData,
        };

        featureFlagsHelper.init({ props });

        _analyticsService = analyticsServiceFactory({
            props,
            getEventId,
        });
        _analyticsService.trackInitialPageView();
        trackAnalyticsAction({ action: 'ReactPickASeatLoad' });
        const hasWebGL = isWebGLPresent();

        if (!hasWebGL) {
            trackAnalyticsAction({ action: 'webglUnavailable' });
        }
    }

    _initialised = true;
    return Promise.resolve();
};

export default {
    ...appDirector,
    aInit,
};

if (typeof window !== 'undefined') {
    window.debugDirector = appDirector;
}
